/**
 * Scrolls the page to the first form field rendered in an error state.
 *
 * Relies on MUI applying the `Mui-error` class to TextField/Select inputs
 * (and their FormControl wrappers) when `error` is truthy. It is intended to
 * be invoked after a submit handler has called `setErrors(...)` so that the
 * relevant fields have re-rendered with the error class applied.
 *
 * A small delay is used to ensure React has had a chance to flush the state
 * update to the DOM before we query for the error node.
 *
 * @param {Object} [options]
 * @param {HTMLElement|Document} [options.container=document] - Optional root to
 *   search within. Useful when multiple forms are mounted simultaneously.
 * @param {number} [options.delay=50] - Milliseconds to wait before scrolling.
 * @param {boolean} [options.focus=true] - Whether to also focus the first
 *   invalid input after scrolling.
 */
const scrollToFirstError = ({ container, delay = 50, focus = true } = {}) => {
  setTimeout(() => {
    const root = container || document;

    // Prefer the actual input/select wrapper so the scroll lands on the field
    // itself rather than the helper-text element (which also gets Mui-error).
    let firstError = root.querySelector(
      '.MuiInputBase-root.Mui-error, .MuiFormControl-root.Mui-error'
    );

    if (!firstError) {
      firstError = root.querySelector('.Mui-error');
    }

    if (!firstError) return;

    if (typeof firstError.scrollIntoView === 'function') {
      firstError.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }

    if (focus) {
      const input = firstError.querySelector(
        'input, textarea, [role="button"]'
      );
      if (input && typeof input.focus === 'function') {
        // Defer focus slightly so the smooth scroll has time to start before
        // the browser potentially overrides it with a focus-driven jump.
        setTimeout(() => {
          try {
            input.focus({ preventScroll: true });
          } catch (e) {
            input.focus();
          }
        }, 200);
      }
    }
  }, delay);
};

export default scrollToFirstError;